import { useState, useEffect } from 'react';
import { History, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { DiagnosticResult } from './DiagnosticResult';

interface Diagnostic {
  id: string;
  image_url: string;
  health_score: number;
  problems: Array<{
    name: string;
    severity: 'low' | 'moderate' | 'high';
    description: string;
    solution: string;
  }>;
  recommendations: string;
  created_at: string;
}

interface DiagnosticHistoryProps {
  plantId: string;
  refreshTrigger?: number;
}

export function DiagnosticHistory({ plantId, refreshTrigger }: DiagnosticHistoryProps) {
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDiagnostic, setSelectedDiagnostic] = useState<Diagnostic | null>(null);

  useEffect(() => {
    fetchDiagnostics();
  }, [plantId, refreshTrigger]);

  const fetchDiagnostics = async () => {
    try {
      const { data, error } = await supabase
        .from('plant_diagnostics')
        .select('*')
        .eq('plant_id', plantId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar diagnósticos:', error);
        return;
      }

      // Cast the data to handle JSONB properly
      const formattedData = data?.map(d => ({
        ...d,
        problems: (d.problems as Diagnostic['problems']) || []
      })) || [];

      setDiagnostics(formattedData);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Histórico de Diagnósticos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (selectedDiagnostic) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelectedDiagnostic(null)}
          className="flex items-center gap-2 text-sm text-primary hover:underline"
        >
          ← Voltar ao histórico
        </button>
        <DiagnosticResult
          healthScore={selectedDiagnostic.health_score}
          problems={selectedDiagnostic.problems}
          recommendations={selectedDiagnostic.recommendations}
          imageUrl={selectedDiagnostic.image_url}
          createdAt={selectedDiagnostic.created_at}
        />
      </div>
    );
  }

  if (diagnostics.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Histórico de Diagnósticos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhum diagnóstico realizado ainda
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          Histórico de Diagnósticos ({diagnostics.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {diagnostics.map((diagnostic) => (
          <button
            key={diagnostic.id}
            onClick={() => setSelectedDiagnostic(diagnostic)}
            className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
          >
            <img
              src={diagnostic.image_url}
              alt="Diagnóstico"
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={getHealthColor(diagnostic.health_score)}>
                  {diagnostic.health_score}%
                </Badge>
                {diagnostic.problems.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {diagnostic.problems.length} problema(s)
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {new Date(diagnostic.created_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
